import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase/client'
import { generatePDF } from '@/services/etPDFTemplate'
import { Upload, X } from 'lucide-react'
import type { ETFormData, ETInspector, ETCompany } from '@/types/etTypes'
import { DEFAULT_ET_PROBE_SETTINGS, ET_DEFAULT_TEST_SITE, ET_TEST_SITES, inferETObjectType } from '@/types/etTypes'
import { getNextMethodReportSequence } from '@/utils/getNextReportSequence'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import {
  attachNdtReportToEquipment,
  fetchAttachedR1R3Slings,
  fetchAttachedSTMIDBR1R3Slings,
  type AttachedSling,
} from '@/utils/ndtEquipmentReport'
import NdtFormPage, { NdtFormActions, NdtStatusButtons, ndtInputCls, ndtLabelCls, ndtSelectCls, ndtTextareaCls, ndtToggleCls, ndtUploadCls, ndtPrimaryBtnCls, ndtSecondaryBtnCls } from '@/components/ndt/NdtFormPage'
import NdtEquipmentSearch, { type NdtEquipmentHit } from '@/components/ndt/NdtEquipmentSearch'

// Minimal ET Test Form
const ETTestForm = () => {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inspectors, setInspectors] = useState<ETInspector[]>([])
  const [admins, setAdmins] = useState<{ id: string; full_name: string }[]>([])
  const [companies, setCompanies] = useState<ETCompany[]>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null)
  const [attachedSlings, setAttachedSlings] = useState<AttachedSling[]>([])
  const [inspectionKind, setInspectionKind] = useState<'NDT' | '48 måneder'>('NDT')
  
  const [formData, setFormData] = useState<ETFormData>({
    testDate: new Date().toISOString().split('T')[0],
    testSite: ET_DEFAULT_TEST_SITE,
    reportNumber: '',
    unitId: '',
    slingId: '',
    isChainSling: false,
    issuedBy: '',
    verifiedBy: '',
    status: 'Accepted',
    objectType: 'POU',
    customObjectType: undefined,
    material: 'Carbon steel',
    company: '',
    company_id: '',
    report_code: null,
    comments: '',
    attachedImages: []
  })

  useEffect(() => {
    fetchInspectorsAndCompanies()
  }, [navigate])

  const fetchInspectorsAndCompanies = async () => {
    try {
      // Get current logged-in user
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) return
      
      // Get user's profile to check role and supervisor status
      const { data: userProfile } = await supabase
        .from('profiles')
        .select('role, full_name, supervisor')
        .eq('id', user.id)
        .single()
      
      const isUserAdmin = userProfile?.role === 'admin' || userProfile?.supervisor === true
      setIsAdmin(isUserAdmin)
      
      const [inspectorsRes, companiesRes, adminsRes] = await Promise.all([
        supabase.from('profiles').select('id, full_name, certificate_no, signature, et_level, et_equipment, role').or('role.eq.inspector,and(role.eq.admin,et_level.gt.0)').order('full_name'),
        supabase.from('companies').select('id, name, report_code').order('name'),
        supabase.from('profiles').select('id, full_name').eq('role', 'admin').order('full_name'),
      ])

      // If user is admin, show all inspectors. If inspector, only show themselves
      if (inspectorsRes.data) {
        if (isUserAdmin) {
          // Admin sees all inspectors
          setInspectors(inspectorsRes.data)
        } else {
          // Non-admin (inspector) only sees themselves
          const currentInspector = inspectorsRes.data.find(inspector => inspector.id === user.id)
          if (currentInspector) {
            setInspectors([currentInspector])
            setFormData(prev => ({ ...prev, issuedBy: currentInspector.full_name }))
          }
        }
      }
      
      if (companiesRes.data) setCompanies(companiesRes.data)
      if (adminsRes.data) {
        setAdmins(
          adminsRes.data
            .filter((row): row is { id: string; full_name: string } => !!row.full_name)
            .map((row) => ({ id: row.id, full_name: row.full_name })),
        )
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const generateOrderNumber = (reportCode: string) => {
    if (!reportCode) return ''
    
    const now = new Date()
    const year = now.getFullYear().toString().slice(-2)
    const month = (now.getMonth() + 1).toString().padStart(2, '0')
    
    return `I${year}${month}${reportCode.padStart(3, '0')}`
  }

  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companies.find(c => c.id === companyId)
    setSelectedEquipmentId(null)
    setAttachedSlings([])
    if (selectedCompany) {
      const orderNumber = generateOrderNumber(selectedCompany.report_code || '')
      setFormData(prev => ({
        ...prev,
        company: selectedCompany.name,
        company_id: selectedCompany.id,
        report_code: selectedCompany.report_code,
        reportNumber: orderNumber
      }))
    }
  }

  const handleSelectEquipment = async (equipment: NdtEquipmentHit | null) => {
    setSelectedEquipmentId(equipment?.id ?? null)
    if (!equipment) {
      setAttachedSlings([])
      return
    }

    let categoryCode: string | null
    let slings: AttachedSling[]
    if (equipment.source === 'stmidb') {
      categoryCode = equipment.category_code ?? null
      slings = await fetchAttachedSTMIDBR1R3Slings(equipment.id)
    } else {
      const [{ data: eqRow }, wllSlings] = await Promise.all([
        supabase
          .from('equipment_v2')
          .select('categories_v2(code)')
          .eq('id', equipment.id)
          .single(),
        fetchAttachedR1R3Slings(equipment.id),
      ])
      const categoryRow = eqRow?.categories_v2 as { code?: string } | { code?: string }[] | null | undefined
      categoryCode = (Array.isArray(categoryRow) ? categoryRow[0]?.code : categoryRow?.code) || null
      slings = wllSlings
    }
    const inferred = inferETObjectType(categoryCode, equipment.name)
    const serial = equipment.serial_number || ''
    const isSling = inferred.objectType === 'Sling'

    setAttachedSlings(isSling ? [] : slings)
    setFormData(prev => {
      const keepSling = prev.objectType === 'Sling'
      return {
        ...prev,
        objectType: keepSling ? 'Sling' : (inferred.objectType === 'custom' ? 'POU' : inferred.objectType),
        customObjectType: undefined,
        unitId: (keepSling || isSling) ? '' : serial,
        slingId: (keepSling || isSling) ? serial : slings.map(s => s.serial).join(' & '),
        isChainSling: (keepSling || isSling) ? inferred.isChainSling : slings.some(s => s.isChain),
      }
    })
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    Array.from(files).forEach(file => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64 = reader.result as string
        const newImage = {
          id: Date.now().toString() + Math.random(),
          image: base64,
          comment: ''
        }
        setFormData(prev => ({
          ...prev,
          attachedImages: [...prev.attachedImages, newImage]
        }))
      }
      reader.readAsDataURL(file)
    })
  }

  const updateImageComment = (imageId: string, comment: string) => {
    setFormData(prev => ({
      ...prev,
      attachedImages: prev.attachedImages.map(img =>
        img.id === imageId ? { ...img, comment } : img
      )
    }))
  }

  const removeImage = (imageId: string) => {
    setFormData(prev => ({
      ...prev,
      attachedImages: prev.attachedImages.filter(img => img.id !== imageId)
    }))
  }

  const buildPdfPayload = (sequenceNumber: string) => ({
    ...formData,
    probeSettings: DEFAULT_ET_PROBE_SETTINGS,
    certificationNumber: inspectors.find(i => i.full_name === formData.issuedBy)?.certificate_no || '',
    sequenceNumber,
    verifiedBy: formData.verifiedBy,
    approvedBy: formData.verifiedBy || undefined,
    approvedAt: formData.verifiedBy ? formData.testDate : undefined,
  })

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
  }

  const handlePreview = async () => {
    setIsGeneratingPreview(true)
    try {
      const { sequenceNumber } = await getNextMethodReportSequence('ET', formData.reportNumber)
      const blob = await generatePDF(buildPdfPayload(sequenceNumber))
      closePreview()
      setPreviewUrl(URL.createObjectURL(blob))
    } catch (error) {
      console.error(error)
      alert('Kunne ikke generere forhåndsvisning.')
    } finally {
      setIsGeneratingPreview(false)
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    setIsSubmitting(true)

    try {
      // Get sequence number
      const { data: existingFiles, error: listError } = await supabase
        .storage
        .from('reports')
        .list(`${formData.reportNumber}`)

      if (listError && listError.message !== 'Bucket not found') {
        throw listError
      }

      const sequenceNumber = existingFiles ? existingFiles.length + 1 : 1
      const paddedSequence = String(sequenceNumber).padStart(3, '0')
      const fileName = `${formData.reportNumber}_${paddedSequence}.pdf`
      const folderPath = `${formData.reportNumber}/${fileName}`

      const pdfBlob = await generatePDF(buildPdfPayload(paddedSequence))

      // Upload to storage
      const { error: uploadError } = await supabase
        .storage
        .from('reports')
        .upload(folderPath, pdfBlob, {
          contentType: 'application/pdf',
          cacheControl: '3600'
        })

      if (uploadError) throw uploadError

      // Prepare unit_id based on object type
      const unitIdForDb = formData.objectType === 'Sling' 
        ? (formData.slingId || 'Sling') 
        : formData.unitId || 'N/A'

      // Store metadata with full report data for editing/regeneration
      const { error: metadataError } = await supabase
        .from('ndt_reports')
        .insert({
          order_number: `ET-${formData.reportNumber}.${paddedSequence}`,
          unit_id: unitIdForDb,
          test_date: formData.testDate,
          test_type: 'ET',
          status: formData.status,
          issued_by: formData.issuedBy,
          verified_by: formData.verifiedBy?.trim() || null,
          file_path: folderPath,
          report_code: formData.report_code,
          company_id: formData.company_id,
          equipment_id: selectedEquipmentId || null,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          report_data: {
            ...formData,
            probeSettings: DEFAULT_ET_PROBE_SETTINGS,
            certificationNumber: inspectors.find(i => i.full_name === formData.issuedBy)?.certificate_no || '',
            sequenceNumber: paddedSequence,
            equipmentId: selectedEquipmentId,
            attachedSlings,
            inspectionKind,
          }
        })

      if (metadataError) throw metadataError

      if (selectedEquipmentId) {
        const serialNumber = formData.unitId || formData.slingId || formData.reportNumber
        const updateNdtDates = inspectionKind === '48 måneder'
        await attachNdtReportToEquipment({
          pdfBlob,
          fileName,
          customerName: formData.company || 'unknown',
          serialNumber,
          equipmentId: selectedEquipmentId,
          testDate: formData.testDate,
          reportStatus: formData.status,
          updateNdtDates,
        })
        for (const sling of attachedSlings) {
          if (sling.equipmentId === selectedEquipmentId) continue
          await attachNdtReportToEquipment({
            pdfBlob,
            fileName: `${sling.serial}_${fileName}`,
            customerName: formData.company || 'unknown',
            serialNumber: sling.serial,
            equipmentId: sling.equipmentId,
            testDate: formData.testDate,
            reportStatus: formData.status,
            updateNdtDates,
          })
        }
      }

      // Create signed URL and open
      const { data, error: signedUrlError } = await supabase
        .storage
        .from('reports')
        .createSignedUrl(folderPath, 60)

      if (signedUrlError) throw signedUrlError
      if (!data?.signedUrl) throw new Error('No signed URL returned')

      window.open(data.signedUrl, '_blank')
      
      alert(
        selectedEquipmentId && inspectionKind === '48 måneder'
          ? 'Rapport generert, lagret på utstyr og NDT-datoer oppdatert.'
          : selectedEquipmentId
            ? 'Rapport generert og lagret på utstyr.'
            : 'Rapport generert og lagret.',
      )
      
      // Navigate back to NDT selection
      navigate(getNdtExitPath())
    } catch (error) {
      console.error('Error generating report:', error)
      alert('Error generating report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <NdtFormPage
      highlight="Eddy Current"
      title="Testing"
      previewUrl={previewUrl}
      previewTitle="ET-rapport forhåndsvisning"
      onClosePreview={closePreview}
      onPublish={() => void handleSubmit()}
      isPublishing={isSubmitting}
      canPublish={!!formData.reportNumber.trim() && !!formData.issuedBy.trim()}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={ndtLabelCls}>Kontrolltype</label>
            <div className="flex gap-2">
              {([
                { value: 'NDT' as const, label: 'NDT' },
                { value: '48 måneder' as const, label: '48 måneder' },
              ]).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setInspectionKind(option.value)}
                  className={ndtToggleCls(inspectionKind === option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {inspectionKind === '48 måneder' && (
              <p className="mt-1.5 text-xs text-slate-400">
                Oppdaterer NDT-dato for valgt utstyr ved publisering.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={ndtLabelCls}>Testdato</label>
              <input
                type="date"
                value={formData.testDate}
                onChange={(e) => setFormData(prev => ({ ...prev, testDate: e.target.value }))}
                required
                className={ndtInputCls}
              />
            </div>
            <div>
              <label className={ndtLabelCls}>Teststed</label>
              <select
                value={formData.testSite}
                onChange={(e) => setFormData(prev => ({ ...prev, testSite: e.target.value }))}
                className={ndtSelectCls}
              >
                {ET_TEST_SITES.map((site) => (
                  <option key={site} value={site}>{site}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={ndtLabelCls}>Kunde</label>
              <select
                value={formData.company_id}
                onChange={(e) => handleCompanyChange(e.target.value)}
                required
                className={ndtSelectCls}
              >
                <option value="">Velg kunde</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={ndtLabelCls}>Vårt ordrenummer</label>
              <input
                type="text"
                required
                value={formData.reportNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, reportNumber: e.target.value }))}
                className={ndtInputCls}
                placeholder="STM ordrenummer"
              />
            </div>
          </div>

          <div>
            <label className={ndtLabelCls}>Objekttype</label>
            <div className="flex gap-2">
              {([
                { value: 'POU' as const, label: 'POU' },
                { value: 'POU w/ISO' as const, label: 'POU w/ISO' },
                { value: 'Sling' as const, label: 'Sling' },
              ]).map((option) => {
                const selected = formData.objectType === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      setSelectedEquipmentId(null)
                      setAttachedSlings([])
                      setFormData((prev) => ({
                        ...prev,
                        objectType: option.value,
                        customObjectType: undefined,
                        unitId: option.value === 'Sling' ? '' : prev.unitId,
                        slingId: option.value === 'Sling' ? '' : prev.slingId,
                      }))
                    }}
                    className={ndtToggleCls(selected)}
                  >
                    {option.label}
                  </button>
                )
              })}
            </div>
          </div>

          <NdtEquipmentSearch
            key={`${formData.company_id || 'none'}-${formData.objectType}`}
            companyId={formData.company_id || ''}
            categoryPrefix={formData.objectType === 'Sling' ? undefined : 'F'}
            categoryPrefixes={formData.objectType === 'Sling' ? ['R1', 'R3'] : undefined}
            hint={formData.objectType === 'Sling'
              ? 'Søk i R1- og R3-kategori (chain- og wire-slings)'
              : 'Søk i F-kategori. Tilknyttede R1/R3-slings hentes automatisk'}
            emptyMessage={formData.objectType === 'Sling'
              ? 'Ingen R1/R3-utstyr funnet'
              : 'Ingen F-kategori utstyr funnet'}
            onSelect={(eq) => void handleSelectEquipment(eq)}
          />
          {attachedSlings.length > 0 && (
            <p className="text-xs text-slate-400 -mt-2">
              Importert sling: {attachedSlings.map(s => s.serial).join(' · ')}
            </p>
          )}

          {isAdmin && (
            <div>
              <label className={ndtLabelCls}>Utført av</label>
              <select
                value={formData.issuedBy}
                onChange={(e) => setFormData(prev => ({ ...prev, issuedBy: e.target.value }))}
                required
                className={ndtSelectCls}
              >
                <option value="">Velg inspektør</option>
                {inspectors.map(inspector => (
                  <option key={inspector.id} value={inspector.full_name}>{inspector.full_name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {formData.objectType === 'Sling' ? (
              <div>
                <label className={ndtLabelCls}>Slingtype</label>
                <div className="flex gap-2">
                  {([
                    { value: false, label: 'Wire' },
                    { value: true, label: 'Chain' },
                  ]).map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, isChainSling: option.value }))}
                      className={`flex-1 px-3 py-2 text-sm font-semibold rounded-md border leading-normal ${
                        formData.isChainSling === option.value
                          ? 'border-[#1E3A5F] bg-[#1E3A5F] text-white'
                          : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className={ndtLabelCls}>Unit ID</label>
                <input
                  type="text"
                  value={formData.unitId}
                  onChange={(e) => setFormData(prev => ({ ...prev, unitId: e.target.value }))}
                  required={formData.objectType !== 'custom'}
                  className={ndtInputCls}
                />
              </div>
            )}
            <div>
              <label className={ndtLabelCls}>
                Sling ID {formData.objectType === 'Sling' ? '' : <span className="normal-case tracking-normal font-normal text-slate-300">(valgfritt)</span>}
              </label>
              <input
                type="text"
                value={formData.slingId}
                onChange={(e) => setFormData(prev => ({ ...prev, slingId: e.target.value }))}
                required={formData.objectType === 'Sling'}
                className={ndtInputCls}
              />
            </div>
          </div>

          {formData.objectType !== 'Sling' && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="isChainSling"
                checked={formData.isChainSling}
                onChange={(e) => setFormData(prev => ({ ...prev, isChainSling: e.target.checked }))}
                className="w-5 h-5 accent-[#1E3A5F] rounded border-slate-300"
              />
              <label htmlFor="isChainSling" className="text-sm font-semibold text-slate-800 cursor-pointer">
                Kjettingsling
              </label>
            </div>
          )}

          {/* Status */}
          <div>
            <label className={ndtLabelCls}>Ordreansvarlig</label>
            <select
              value={formData.verifiedBy || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, verifiedBy: e.target.value }))}
              className={ndtSelectCls}
            >
              <option value="">Ingen</option>
              {admins.map(admin => (
                <option key={admin.id} value={admin.full_name}>{admin.full_name}</option>
              ))}
            </select>
          </div>

          <NdtStatusButtons
            value={formData.status}
            onChange={(status) => setFormData(prev => ({ ...prev, status }))}
          />

          {/* Comments (only if rejected) */}
          {formData.status === 'Not Accepted' && (
            <div>
              <label className={ndtLabelCls}>Kommentarer</label>
              <textarea
                value={formData.comments}
                onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                rows={4}
                className={ndtTextareaCls}
                placeholder="Beskriv årsaken til avvisning..."
              />
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className={ndtLabelCls}>Bilder</label>
            <label className={ndtUploadCls}>
              <Upload size={16} className="text-slate-400" />
              <span className="text-sm text-slate-600">Last opp bilder</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
            </label>
          </div>

          {/* Attached Images */}
          {formData.attachedImages.length > 0 && (
            <div className="space-y-4">
              {formData.attachedImages.map((img) => (
                <div key={img.id} className="p-3 border border-slate-200 rounded-md bg-white">
                  <div className="flex items-start gap-3">
                    <img
                      src={img.image}
                      alt="Uploaded"
                      className="w-16 h-16 object-cover rounded-md border border-slate-200"
                    />
                    <div className="flex-1">
                      <input
                        type="text"
                        value={img.comment || ''}
                        onChange={(e) => updateImageComment(img.id, e.target.value)}
                        placeholder="Legg til kommentar..."
                        className={ndtInputCls}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeImage(img.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <NdtFormActions>
            <button
              type="button"
              onClick={() => void handlePreview()}
              disabled={isSubmitting || isGeneratingPreview}
              className={ndtSecondaryBtnCls}
            >
              {isGeneratingPreview ? 'Genererer…' : 'Forhåndsvis'}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isGeneratingPreview}
              className={ndtPrimaryBtnCls}
            >
              {isSubmitting ? 'Genererer rapport...' : 'Generer ET Rapport'}
            </button>
          </NdtFormActions>
        </form>
    </NdtFormPage>
  )
}

export default ETTestForm

